import React from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useParams, useNavigate } from 'react-router-dom';
import useWizardStep from '../components/shared/useWizardStep';
import GraveWizard from '../components/grave_wizard/GraveWizard';
import WizardPage from '../components/shared/WizardPage';
import useAsyncTask from '../components/shared/useAsyncTask';
import useNextResource from '../components/shared/useNextResource';
import { saveScale } from '../components/shared/wizardUtils';
import {
  UPDATE_GRAVE,
  UPDATE_GRAVE_TAGS,
  UPDATE_GRAVE_SCALE,
  UPDATE_SCALE_CONTOUR,
  UPDATE_SKELETON_FIGURE,
  UPDATE_ARROW,
  UPDATE_FIGURE,
  RECALCULATE_GRAVE
} from '../graphql/mutations';
import {
  GRAVE_FOR_EDIT_QUERY,
  GRAVES_QUERY,
  SITES_QUERY,
  TAGS_QUERY,
  NEXT_GRAVE_QUERY
} from '../graphql/queries';

export default function GraveEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useWizardStep('data');
  const task = useAsyncTask();

  const {
    data: graveData,
    loading: queryLoading,
    error: queryError
  } = useQuery(GRAVE_FOR_EDIT_QUERY, { variables: { id }, skip: !id });
  const { data: sitesData } = useQuery(SITES_QUERY);
  const { data: tagsData } = useQuery(TAGS_QUERY);

  const refetch = { refetchQueries: [{ query: GRAVES_QUERY }] };
  const [updateGrave] = useMutation(UPDATE_GRAVE, refetch);
  const [updateGraveTags] = useMutation(UPDATE_GRAVE_TAGS, refetch);
  const [updateGraveScale] = useMutation(UPDATE_GRAVE_SCALE, refetch);
  const [updateFigure] = useMutation(UPDATE_FIGURE, refetch);
  const [updateScaleContour] = useMutation(UPDATE_SCALE_CONTOUR, refetch);
  const [updateSkeletonFigure] = useMutation(UPDATE_SKELETON_FIGURE, refetch);
  const [updateArrow] = useMutation(UPDATE_ARROW, refetch);
  const [recalculateGrave] = useMutation(RECALCULATE_GRAVE, {
    refetchQueries: [{ query: GRAVE_FOR_EDIT_QUERY, variables: { id } }, { query: GRAVES_QUERY }]
  });

  const grave = graveData?.grave;
  const goNextGrave = useNextResource({
    query: NEXT_GRAVE_QUERY,
    variables: { id, publicationId: grave?.publicationId },
    nextKey: 'nextGrave',
    pathFor: (next) => `/graves/${next.id}/edit`,
    fallbackPath: '/graves'
  });

  const handleUpdateGrave = (data) =>
    task.run(() => updateGrave({ variables: { input: { id, ...data } } }));
  const handleUpdateTags = (tagIds) =>
    task.run(() => updateGraveTags({ variables: { input: { id, tagIds } } }));
  const handleUpdateFigures = (inputs) =>
    task.run(() => Promise.all(inputs.map((input) => updateFigure({ variables: { input } }))));
  const handleUpdateSkeletons = (skeletonUpdates) =>
    task.run(() =>
      Promise.all(
        skeletonUpdates.map(({ skeletonId, type }) =>
          updateSkeletonFigure({
            variables: { input: { id: skeletonId, depositionType: parseInt(type, 10) } }
          })
        )
      )
    );
  const handleUpdateArrow = (arrowId, angle) =>
    task.run(() =>
      updateArrow({ variables: { input: { id: arrowId, angle: parseFloat(angle) } } })
    );
  const handleRecalculate = () =>
    task.run(() => recalculateGrave({ variables: { input: { id } } }));
  const handleUpdateScale = (data) =>
    task.run(
      () => saveScale({ data, resourceId: id, updateScale: updateGraveScale, updateFigure }),
      'Failed to update scale'
    );
  // Must resolve to the updated contour (the SAM selector awaits it).
  const handleUpdateScaleContour = (scaleId, points) =>
    updateScaleContour({ variables: { id: scaleId, points } }).then(
      (result) => result.data.updateScaleContour.contour
    );

  return (
    <WizardPage
      breadcrumb={
        grave
          ? [{ label: 'Graves', link: '/graves' }, { label: grave.identifier || `Grave ${id}` }]
          : []
      }
      loading={queryLoading}
      error={queryError}
      resource={grave}
      notFoundLabel="Grave not found"
      task={task}
    >
      <GraveWizard
        step={step}
        setStep={setStep}
        grave={grave}
        sites={sitesData?.sites || []}
        tags={tagsData?.tags || []}
        onUpdateGrave={handleUpdateGrave}
        onUpdateTags={handleUpdateTags}
        onUpdateScale={handleUpdateScale}
        onUpdateScaleContour={handleUpdateScaleContour}
        onUpdateSkeletons={handleUpdateSkeletons}
        onUpdateFigures={handleUpdateFigures}
        onUpdateArrow={handleUpdateArrow}
        onRecalculate={handleRecalculate}
        onCancel={() => navigate(-1)}
        onFinish={goNextGrave}
        loading={task.busy || queryLoading}
      />
    </WizardPage>
  );
}
